import { computed, toValue, type MaybeRefOrGetter } from "vue";
import type { ActivityResource } from "@carnet/api/backoffice";
import { isSchoolingMode } from "../utils/activity";

export function useAppActivityPermissions(activity: MaybeRefOrGetter<ActivityResource | null | undefined>, currentMemberId?: MaybeRefOrGetter<string | null>) {
  const { t } = useI18n();
  const memberId = currentMemberId ?? useCurrentMemberId();

  const isCreator = computed(() => {
    const me = toValue(memberId);
    return me != null && toValue(activity)?.creatorMember?.id === me;
  });

  const isSupervisor = computed(() => {
    const me = toValue(memberId);
    return me != null && toValue(activity)?.supervisorMember?.id === me;
  });

  const isSchooling = computed(() => isSchoolingMode(toValue(activity)?.mode?.code));

  const isSupervised = computed(() => isSupervisedMode(toValue(activity)?.mode?.code));

  // Invité : ni créateur ni encadrant de l'activité (distinct du « non-créateur » de la suppression).
  const isGuest = computed(() => toValue(activity) != null && toValue(memberId) != null && !isCreator.value && !isSupervisor.value);

  const isActiveParticipant = computed(() => {
    const me = toValue(memberId);
    if (me == null) return false;
    return (toValue(activity)?.participations ?? []).some(
      (participation) => participation.member.id === me && (participation.invitationStatus === "accepted" || participation.invitationStatus === "invited")
    );
  });

  const canInviteParticipants = computed(() => isCreator.value || isSupervisor.value || isActiveParticipant.value);

  // Non-créateur confirmé (suppression) : l'endpoint ne retire alors que sa participation. Fail-safe
  // prudent — en cas de doute (créateur inconnu ou membre non résolu) on reste sur false, donc sur
  // l'avertissement créateur (suppression définitive), le message le plus prudent.
  const isDeleteGuest = computed(() => {
    const creatorId = toValue(activity)?.creatorMember?.id;
    const me = toValue(memberId);
    return creatorId != null && me != null && creatorId !== me;
  });

  const hasValidatedParticipations = computed(() => !!toValue(activity)?.hasValidatedParticipations);

  const isLockedAsGuest = computed(() => isSchooling.value && isGuest.value);

  const isLockedByValidation = computed(() => isSchooling.value && toValue(memberId) != null && !isSupervisor.value && hasValidatedParticipations.value);

  const modeLocked = computed(() => {
    return isGuest.value || (isSchooling.value && toValue(memberId) != null && hasValidatedParticipations.value);
  });

  const mainInfosLocked = computed(() => isLockedAsGuest.value || isLockedByValidation.value);

  const lockedMessage = computed(() => {
    if (isLockedAsGuest.value) return t("activity.edit.locked.guest.message");
    if (isLockedByValidation.value) return t("activity.edit.locked.message");
    return "";
  });

  return { isCreator, isSupervisor, canInviteParticipants, isSupervised, isSchooling, isGuest, isDeleteGuest, hasValidatedParticipations, mainInfosLocked, lockedMessage, modeLocked };
}
